/*
 * Class that contains the image and associated regions.
 */

import { EvalPass, HoveredFromState, Marker } from "../../api/EvalWorkflowApi";
import { ImageResult } from "../../api/ImageResultApi";
import { LabelObsStudy } from "../../api/LabelizeApi";
import {
  AiPass,
  Image,
  Negative,
  Observation,
  PassMarker,
  PassRegion,
  Region,
  RegionGroup,
  RegionType,
  Study,
} from "../../api/StudyApi";
import LocalStorageService from "../../service/LocalStorageService";

const PASS_COLORS = [
  "#E225FF",
  "#FCF2A5",
  "#13A0EB",
  "#CEB4F8",
  "#EDFF25",
  "#B3FEE8",
  "#FBBBBB",
  "#0CB523",
  "#ADDFFF",
  "#133EEB",
  "#D2FDAA",
  "#FF2584",
  "#FFCB33",
  "#ACD1B1",
  "#FABCF4",
  "#13EBE1",
  "#7725FF",
  "#FF7733",
  "#6CFF25",
  "#FF1A1A",
  "#FFCC89",
];

export default class ViewportImage {
  readonly image: Image;
  regions = new Array<Region>();
  passRegions? = new Array<PassRegion>();
  markers = new Array<Marker>();
  passMarkers = new Array<PassMarker>();
  readonly result: boolean;
  selected = false;

  constructor(img: Image, regions: Region[], isResultImage = false, selected = false) {
    this.image = img;
    this.regions.push(...regions);
    this.result = isResultImage;
    this.selected = selected;
  }

  get id() {
    return this.image.id;
  }

  get path(): string {
    return this.image.path;
  }

  get aiPasses(): Array<AiPass> {
    return this.image.aiPasses;
  }

  is(obj: any) {
    return obj instanceof ViewportImage && obj.image.id == this.image.id;
  }

  attenuateOtherRegions(obsIdx: number): void {
    this.regions.filter((reg) => reg.obsIdx && reg.obsIdx !== obsIdx).forEach((reg) => (reg.attenuated = true));
  }

  resetAttenuation() {
    this.regions.forEach((reg) => {
      reg.attenuated = false;
    });
  }

  activateRegion(region: Region): ViewportImage {
    region.active = true;
    return this;
  }

  deactivate() {
    this.regions.forEach((reg) => (reg.active = false));
    return this;
  }

  hoveredMarker(marker: Marker, isIn: boolean, from: HoveredFromState) {
    this.markers = this.markers.map((mrk) => {
      if (marker._label === mrk._label) {
        return { ...mrk, _hoveredFrom: isIn ? from : null };
      }
      return mrk;
    });
    return this;
  }

  select() {
    this.selected = true;
    return this;
  }

  deselect() {
    this.selected = false;
    return this;
  }

  static fromMarkers(images: Array<Image>, markers: Marker[]) {
    return images.map((img) => {
      const viewportImage = new ViewportImage(img, []);
      viewportImage.markers = markers.filter((m) => m.imageId === img.id);
      return viewportImage;
    });
  }

  static fromImageResults(imageResults: Array<ImageResult>) {
    return imageResults.map((imgr) => {
      return new ViewportImage(
        {
          id: imgr.path,
          aiPasses: [],
          path: imgr.path,
          thumbnailPath: imgr.thumbnailPath,
          tags: [],
        },
        [],
        true
      );
    });
  }

  static fromObservations(
    observations: Array<RegionGroup>,
    images: Array<Image>,
    prevImages: ViewportImage[] = [],
    obsType = RegionType.OBSERVATION
  ): Array<ViewportImage> {
    let viewportImages: ViewportImage[] = [];

    if (prevImages.length == 0) {
      images.forEach((img) => {
        const imgElt = new ViewportImage(img, ViewportImage.extractRegionsFromObservations(img, observations, obsType));
        viewportImages.push(imgElt);
      });
    } else {
      viewportImages = prevImages;

      // Update one-by-one to keep order
      for (let i = 0; i < viewportImages.length; i++) {
        images.forEach((img) => {
          if (viewportImages[i].id == img.id) {
            viewportImages[i] = new ViewportImage(
              img,
              ViewportImage.extractRegionsFromObservations(img, observations, obsType),
              false,
              viewportImages[i].selected
            );
          }
        });
      }
    }

    return viewportImages;
  }

  //TODO refactor using upper method
  static fromStudy(study: Study | LabelObsStudy, prevImages: ViewportImage[] = []): Array<ViewportImage> {
    let viewportImages: ViewportImage[] = [];

    if (prevImages.length == 0) {
      study.series.forEach((s) =>
        s.images.forEach((img) => {
          const imgElt = new ViewportImage(img, ViewportImage.extractRegions(study, img));
          viewportImages.push(imgElt);
        })
      );
    } else {
      viewportImages = prevImages;

      // Update one-by-one to keep order
      for (let i = 0; i < viewportImages.length; i++) {
        study.series.forEach((s) =>
          s.images.forEach((img) => {
            if (viewportImages[i].id == img.id) {
              // todo use a update function instead
              const selected = viewportImages[i].selected || false;

              viewportImages[i] = new ViewportImage(img, this.extractRegions(study, img));
              viewportImages[i].selected = selected;
            }
          })
        );
      }
    }

    return viewportImages;
  }

  private static extractRegionsFromObservations(
    img: Image,
    observations: Array<RegionGroup>,
    obsType = RegionType.OBSERVATION
  ): Region[] {
    return observations.reduce((arr, obs, idx) => {
      arr.push(
        ...obs.regions
          .filter((r) => r.imageId === img.id)
          .map((r) => {
            r._type = obsType;
            r.obsIdx = idx;
            r.badge = obs._badge;
            return r;
          })
      );

      return arr;
    }, []);
  }

  private static extractRegionsFromNegatives(img: Image, negatives: Array<Negative>, baseObsIdx: number): Region[] {
    return (negatives || []).reduce((arr, neg, idx) => {
      const ot = LocalStorageService.getObservationType(neg.type);

      const color = ot && ot.negativeColor ? ot.negativeColor : "#FFF";

      arr.push(
        ...neg.regions
          .filter((r) => r.imageId === img.id)
          .map((r) => {
            r._type = RegionType.NEGATIVE;
            r.obsIdx = baseObsIdx + idx;
            r.badge = { color, label: "NO " + neg.type.toUpperCase() };
            r.availableTags = ot.negativeTags;

            return r;
          })
      );

      return arr;
    }, []);
  }

  private static extractRegions(
    study: { observations: Array<Observation>; negatives?: Array<Negative> },
    img: Image,
    obsType = RegionType.OBSERVATION
  ): Region[] {
    return this.extractRegionsFromObservations(img, study.observations, obsType).concat(
      this.extractRegionsFromNegatives(img, study.negatives, study.observations.length)
    );
  }

  public static getPassColor(passIdx: number) {
    return PASS_COLORS[passIdx % PASS_COLORS.length];
  }

  public static addPassesToImage(passes: Array<EvalPass>, viewportImages: Array<ViewportImage>) {
    viewportImages.forEach((viewportImage) => {
      passes.forEach((pass, idx) => {
        let passIndex = pass._passIdx ?? idx;

        pass.observations.forEach((obs, obsIdx) => {
          obs.regions.forEach((reg, regIx) => {
            if (reg.imageId === viewportImage.image.id) {
              const badge = {
                color: obs._badge?.color ?? this.getPassColor(idx),
                label: `${passIndex} : ${obs._badge?.label} : ${obs.obsType}`,
              };

              const passRegion = { ...reg, ...{ passIdx: passIndex }, badge: badge };

              viewportImage.passRegions.push(passRegion);
            }
          });
        });

        pass.markers.forEach((marker) => {
          if (marker.imageId === viewportImage.image.id) {
            const passMarker = {
              ...marker,
              ...{ passIdx: passIndex },
              _color: pass._passColor ?? this.getPassColor(passIndex),
            };

            viewportImage.passMarkers.push(passMarker);
          }
        });
      });
    });
  }
}
